import React, { useEffect, useState } from 'react';
import { formatItemAmount, importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import {
  asList,
  getJson,
  integrationError,
  integrationLabel,
  isProjectToolIntegration,
  isSyncableIntegration,
} from '../helpers';

export default function BudgetSyncModal({ projectVuid, onClose, onComplete }) {
  const [integrations, setIntegrations] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [localIcoNumbers, setLocalIcoNumbers] = useState(new Set());
  const [integrationVuid, setIntegrationVuid] = useState('');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('init');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = isProjectToolIntegration(integration);
  const receiveAllowed = isProjectToolIntegration(integration);

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy('init');
    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/project-budgets', { project_vuid: projectVuid }),
          getJson('/api/internal-change-orders', { project_vuid: projectVuid }).catch(() => []),
    ])
      .then(([integrationRows, budgetRows, icoRows]) => {
        if (cancelled) return;
        const nextIntegrations = asList(integrationRows).filter((row) => {
          if (String(row.status || '').toLowerCase() !== 'active') return false;
          return isSyncableIntegration(row);
        });
        setIntegrations(nextIntegrations);
        setIntegrationVuid(nextIntegrations[0]?.vuid || '');
        setBudgets(asList(budgetRows).filter((row) => row.project_vuid === projectVuid));
        setLocalIcoNumbers(
          new Set(
            asList(icoRows)
              .map((row) => String(row.change_order_number || '').trim())
              .filter(Boolean)
          )
        );
      })
      .catch((err) => {
        if (!cancelled) setFlash({ type: 'err', text: integrationError(err) });
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (isProjectToolIntegration(integration) && nextDirection === 'send') {
        const next = budgets
          .filter((budget) => budget.budget_type === 'original')
          .map((budget) => ({
            id: budget.vuid,
            title: budget.description || 'Original Budget',
            subtitle: [budget.budget_type, formatItemAmount(budget.budget_amount)].filter(Boolean).join(' · '),
            sendKind: 'budget',
            payload: { project_vuid: projectVuid, budget_vuid: budget.vuid },
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else if (isProjectToolIntegration(integration) && nextDirection === 'receive') {
        const [linesSettled, icoSettled] = await Promise.allSettled([
          listFromProcore('budget-lines', integration.vuid, projectVuid),
          listFromProcore('internal-change-orders', integration.vuid, projectVuid),
        ]);
        const warnings = [];
        if (linesSettled.status === 'rejected') {
          warnings.push(`Budget lines: ${integrationError(linesSettled.reason)}`);
        }
        if (icoSettled.status === 'rejected') {
          warnings.push(`Budget changes: ${integrationError(icoSettled.reason)}`);
        }
        const linesResult = linesSettled.status === 'fulfilled' ? linesSettled.value : { items: [], warnings: [] };
        const icoResult = icoSettled.status === 'fulfilled' ? icoSettled.value : { items: [], warnings: [] };
        const lineItems = linesResult.items
          .filter((row) => !row.alreadyImported)
          .map((row) => ({
            id: `line-${row.id}`,
            title: row.title,
            subtitle: ['Budget line', row.subtitle, formatItemAmount(row.amount)].filter(Boolean).join(' · '),
            importKind: 'budget-lines',
            listItem: row,
          }));
        const icoItems = icoResult.items
          .filter((row) => {
            const number = String(row.raw?.change_order_number || row.raw?.number || '').trim();
            return !number || !localIcoNumbers.has(number);
          })
          .map((row) => ({
            id: `ico-${row.id}`,
            title: row.title,
            subtitle: ['Budget change', row.subtitle, formatItemAmount(row.amount)].filter(Boolean).join(' · '),
            importKind: 'internal-change-orders',
            listItem: row,
          }));
        const next = [...lineItems, ...icoItems];
        setItems(next);
        setSelected(next.map((row) => row.id));
        warnings.push(...(linesResult.warnings || []), ...(icoResult.warnings || []));
        if (warnings.length) setFlash({ type: 'err', text: warnings.slice(0, 4).join('\n') });
      } else {
        setItems([]);
        setSelected([]);
      }
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (isProjectToolIntegration(integration) && direction === 'send') {
        for (const row of chosen) {
          try {
            await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      } else if (isProjectToolIntegration(integration) && direction === 'receive') {
        const lineRows = chosen.filter((row) => row.importKind === 'budget-lines').map((row) => row.listItem);
        const icoRows = chosen.filter((row) => row.importKind === 'internal-change-orders').map((row) => row.listItem);
        if (lineRows.length) {
          const result = await importFromProcore('budget-lines', integration.vuid, projectVuid, lineRows);
          sent += lineRows.length;
          (result.errors || []).forEach((item) => errors.push(item));
        }
        if (icoRows.length) {
          const result = await importFromProcore('internal-change-orders', integration.vuid, projectVuid, icoRows);
          sent += icoRows.length;
          (result.errors || []).forEach((item) => errors.push(item));
        }
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="pc-modal-backdrop" onClick={() => busy !== 'load' && busy !== 'sync' && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync budget</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send Claret budgets or receive Procore budget lines and budget changes.
          </p>
          {busy === 'init' ? <div className="pc-empty">Loading integrations…</div> : null}
          {busy !== 'init' && integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : null}
          {busy !== 'init' && integrations.length > 0 ? (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {sendAllowed
                        ? `Push this project’s original budget to ${integrationLabel(integration)}.`
                        : 'Send is only available for the project tool.'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>
                      {receiveAllowed
                        ? `Pull unsynced budget lines and budget changes (internal change orders) from ${integrationLabel(integration)}.`
                        : 'Receive is only available for the project tool.'}
                    </span>
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' && busy !== 'init' ? (
            items.length === 0 ? (
              <div className="pc-empty">No unsynced records for this integration.</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy === 'load' || busy === 'sync'}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={busy === 'load' || busy === 'sync' || busy === 'init' || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
